import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactElement, ReactNode } from 'react';
import { Banner } from './Banner';

/**
 * Two-step confirmation for a destructive command.
 *
 * Requirements it implements, from the spec:
 *  - it states the concrete action and the concrete object, never an abstract
 *    «Вы уверены?»;
 *  - it lists the consequences before anything is sent;
 *  - the second step needs a deliberate act (typing the confirmation word), so
 *    a double-click, a stray Enter or a mis-tap cannot fire a venue command;
 *  - it shows the idempotency key that will be sent, so the same intent
 *    retried is visibly the same command.
 *
 * The dialog is modal for real: focus moves into it, Tab is trapped inside it,
 * and Escape cancels.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  /** One sentence naming exactly what will happen. */
  readonly action: string;
  /** The concrete object of the action: side, size, venue, order count… */
  readonly target: ReactNode;
  readonly consequences: readonly string[];
  /** Word the operator must type on step 2, e.g. `ЗАКРЫТЬ`. */
  readonly confirmWord: string;
  readonly confirmLabel: string;
  readonly tone?: 'danger' | 'primary';
  readonly busy?: boolean;
  readonly errorRu?: string | null;
  readonly idempotencyKey?: string;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  title,
  action,
  target,
  consequences,
  confirmWord,
  confirmLabel,
  tone = 'danger',
  busy = false,
  errorRu = null,
  idempotencyKey,
  onCancel,
  onConfirm,
}: ConfirmDialogProps): ReactElement | null {
  const [step, setStep] = useState<1 | 2>(1);
  const [typed, setTyped] = useState('');
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();
  const inputId = useId();

  useEffect(() => {
    if (open) {
      setStep(1);
      setTyped('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const node = dialogRef.current;
    if (node === null) return;
    const first = node.querySelector<HTMLElement>(FOCUSABLE);
    if (first !== null) first.focus();
  }, [open, step]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        if (!busy) onCancel();
        return;
      }
      if (event.key !== 'Tab') return;
      const node = dialogRef.current;
      if (node === null) return;
      const focusable = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [busy, onCancel],
  );

  if (!open) return null;

  const confirmEnabled = !busy && typed.trim().toUpperCase() === confirmWord.toUpperCase();

  return (
    <div
      className="dialog-backdrop"
      onKeyDown={handleKeyDown}
      data-testid="confirm-dialog-backdrop"
    >
      <div
        className={tone === 'danger' ? 'dialog dialog--danger' : 'dialog'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        ref={dialogRef}
      >
        <h2 className="dialog__title" id={titleId}>
          {title}
        </h2>
        <p className="dialog__steps">Шаг {step} из 2</p>

        <div className="dialog__body" id={descriptionId}>
          <p>
            <strong>Что произойдёт:</strong> {action}
          </p>
          <div>{target}</div>

          {step === 1 && (
            <>
              <p>
                <strong>Последствия:</strong>
              </p>
              <ul className="stack stack--tight">
                {consequences.map((item) => (
                  <li key={item}>— {item}</li>
                ))}
              </ul>
            </>
          )}

          {step === 2 && (
            <>
              <Banner tone="danger" title="Подтверждение действия.">
                Действие необратимо и будет записано в аудит с вашим именем.
              </Banner>
              <div className="field">
                <label className="field__label" htmlFor={inputId}>
                  Для подтверждения введите слово {confirmWord}
                </label>
                <input
                  id={inputId}
                  type="text"
                  autoComplete="off"
                  value={typed}
                  onChange={(event) => {
                    setTyped(event.target.value);
                  }}
                  aria-describedby={`${inputId}-hint`}
                />
                <span className="field__hint" id={`${inputId}-hint`}>
                  Кнопка «{confirmLabel}» станет активной после точного ввода.
                </span>
              </div>
            </>
          )}

          {idempotencyKey !== undefined && (
            <p className="field__hint mono">Ключ идемпотентности: {idempotencyKey}</p>
          )}

          {errorRu !== null && (
            <Banner tone="danger" title="Команда не выполнена.">
              {errorRu}
            </Banner>
          )}
        </div>

        <div className="dialog__actions">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            Отмена
          </button>
          {step === 1 ? (
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => {
                setStep(2);
              }}
            >
              Продолжить
            </button>
          ) : (
            <button
              type="button"
              className={tone === 'danger' ? 'btn btn--danger' : 'btn btn--primary'}
              onClick={onConfirm}
              disabled={!confirmEnabled}
            >
              {busy ? 'Выполняется…' : confirmLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
